// Regions and lines written by non-verbal detection carry a `nonverbal`
// record: label, confidence (0-1), state (applied, suggested or rejected)
// and whether a user has reviewed it. Anything without one is manual.

export const nonverbalOf = (item) =>
    item && typeof item.nonverbal === "object" && item.nonverbal ? item.nonverbal : null;

export const isSuggested = (item) => nonverbalOf(item)?.state === "suggested";

export const isRejected = (item) => nonverbalOf(item)?.state === "rejected";

// Whether the merger puts a region in the dub: "in" for applied regions and
// suggestions detection marked includeByDefault, "held" for suggestions it
// held back (over original speech, say) until a user accepts them, "out" for
// rejected ones. The merger reads the same fields.
export const dubStatus = (item) => {
    const nonverbal = nonverbalOf(item);
    if (!nonverbal || nonverbal.state === "applied") {
        return "in";
    }
    if (nonverbal.state === "rejected") {
        return "out";
    }
    return nonverbal.includeByDefault === true ? "in" : "held";
};

export const HELD_BACK_TEXT = {
    speech: "Original speech can be heard in it",
    tag: "The dub voice also performs this sound from the line's tag",
};

const LABELS = {
    cough: "Cough",
    sneeze: "Sneeze",
    sniff: "Sniff",
    throatClearing: "Throat clearing",
    "throat clearing": "Throat clearing",
    yawn: "Yawn",
    gasp: "Gasp",
    sigh: "Sigh",
    pant: "Panting",
    breathing: "Breathing",
    laughter: "Laugh",
    crying: "Crying",
    whimper: "Whimper",
    scream: "Scream",
    grunt: "Grunt",
    groan: "Groan",
};

export const labelOf = (nonverbal) => {
    const label = nonverbal?.label;
    if (typeof label !== "string" || !label) {
        return "Non-verbal";
    }
    return LABELS[label] || label.charAt(0).toUpperCase() + label.slice(1);
};

export const confidencePercent = (nonverbal) => {
    const confidence = Number(nonverbal?.confidence);
    return Number.isFinite(confidence)
        ? `${Math.round(Math.min(1, Math.max(0, confidence)) * 100)}%`
        : null;
};

// "Cough · 82%", for the badge on a region or line.
export const badgeText = (nonverbal) =>
    [labelOf(nonverbal), confidencePercent(nonverbal)].filter(Boolean).join(" · ");

// AudioShake splits vocalizations between its dialogue and M&E stems without
// overlap, so mixing a separated stem with a client one can double some
// vocalizations (client dialogue) or lose them (client M&E). `stemSources`
// maps each stem type to "provided" or "generated".
export const mixedStemWarning = (stemSources = {}) => {
    const dialogue = stemSources.dialogue;
    const musicEffects = stemSources.musicEffects || stemSources.effect;
    if (dialogue === "provided" && musicEffects === "generated") {
        return "The dialogue is the client's but the M&E was separated, so it may already hold some vocalizations. Splicing them from the dialogue can double them.";
    }
    if (dialogue === "generated" && musicEffects === "provided") {
        return "The dialogue was separated but the M&E is the client's. Separation put some vocalizations in its own M&E, which isn't used, so they are missing from the dialogue and can't be found.";
    }
    return null;
};

// The SFX tab lists applied and manual regions as before, suggestions apart,
// and hides rejected ones unless asked.
export const groupRegions = (regions = []) => ({
    shown: regions.filter((region) => !isSuggested(region) && !isRejected(region)),
    suggested: regions.filter(isSuggested),
    rejected: regions.filter(isRejected),
});
